
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function SearchFilters() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [priceRange, setPriceRange] = useState('All');
  const [industry, setIndustry] = useState('All');

  const categories = ['All', 'AI Agency', 'AIaaS', 'Automation Platform', 'AI Infrastructure'];
  const priceRanges = ['All', '$5K-$25K', '$25K-$100K', '$100K-$500K', '$500K+'];
  const industries = ['All', 'Marketing', 'Healthcare', 'Real Estate', 'Legal', 'E-Commerce', 'Finance'];

  return (
    <section className="py-20 bg-gray-950/50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Search Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-tight">
            Discover AI Agencies
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">
            Browse verified AI service companies and automation agencies ready for acquisition or partnership.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
              <i className="ri-search-line text-gray-500 text-xl"></i>
            </div>
            <input
              type="text"
              placeholder="Search AI agencies, service businesses, or automation companies..."
              className="w-full pl-16 pr-32 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent backdrop-blur-sm text-lg"
              disabled
            />
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
              <div className="flex items-center space-x-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full px-3 py-1">
                <i className="ri-lock-line text-yellow-400 text-sm"></i>
                <span className="text-yellow-400 text-xs font-medium">Members Only</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Categories */}
        <div className="space-y-8">
          {/* Category Filter */}
          <div className="relative">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Category</h3>
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  disabled={category !== 'All'}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 relative ${
                    activeCategory === category
                      ? 'bg-white text-black'
                      : category === 'All'
                      ? 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                      : 'bg-white/5 text-gray-600 border border-white/10 cursor-not-allowed'
                  }`}
                >
                  {category}
                  {category !== 'All' && (
                    <i className="ri-lock-line text-yellow-400 text-xs ml-2"></i>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="relative">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Price Range</h3>
            <div className="flex flex-wrap gap-3">
              {priceRanges.map((range) => (
                <button
                  key={range}
                  onClick={() => setPriceRange(range)}
                  disabled={range !== 'All'}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 relative ${
                    priceRange === range
                      ? 'bg-white text-black'
                      : range === 'All'
                      ? 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                      : 'bg-white/5 text-gray-600 border border-white/10 cursor-not-allowed'
                  }`}
                >
                  {range}
                  {range !== 'All' && (
                    <i className="ri-lock-line text-yellow-400 text-xs ml-2"></i>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Industry Filter */}
          <div className="relative">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Industry</h3>
            <div className="flex flex-wrap gap-3">
              {industries.map((ind) => (
                <button
                  key={ind}
                  onClick={() => setIndustry(ind)}
                  disabled={ind !== 'All'}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 relative ${
                    industry === ind
                      ? 'bg-white text-black'
                      : ind === 'All'
                      ? 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                      : 'bg-white/5 text-gray-600 border border-white/10 cursor-not-allowed'
                  }`}
                >
                  {ind}
                  {ind !== 'All' && (
                    <i className="ri-lock-line text-yellow-400 text-xs ml-2"></i>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex justify-between items-center mt-12 pt-8 border-t border-white/10">
          <div className="text-gray-400">
            <span className="text-white font-medium">6</span> agencies shown • <span className="text-yellow-400 font-medium">141 more</span> available to members
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-400">Sort by:</span>
            <div className="relative">
              <select 
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/20 cursor-not-allowed"
                disabled
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="revenue">Revenue</option>
                <option value="newest">Newest</option>
              </select>
              <i className="ri-lock-line text-yellow-400 text-xs absolute right-3 top-1/2 transform -translate-y-1/2"></i>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
